import React from 'react';
import { Box, Button, SxProps, Theme, Typography } from '@mui/material';
import { useSelector } from "../../redux/hooks";
import { selectSelectedTestkit } from '../../redux/slices/testkit';
import { selectEmail, selectMidwifeNames, selectRole, selectSiteNames } from '../../redux/slices/account';
import { TestkitStatusAPI, getTestkitStatusLabel } from '../../types/testkit';
import { AccountRole } from '../../types/account';
import { isoDateTimeToDate } from '../../utils/dateTime';
import { getStatusTimestampDateTime, getTestkitLocation } from '../../utils/testkit';
import { isPowerAdmin } from '../../utils/auth';
import Section from '../../components/Section';
import Parameter from '../../components/Parameter';
import DialogModifyKit from './DialogModifyKit';
import DialogDiscardKit from './DialogDiscardKit';
// import TestkitAPI from '../../api/endpoints/testkit';

const COL_WIDTH_0 = 100;
const COL_WIDTH_1 = 180;
const COL_WIDTH_2 = 100;
const COL_WIDTH_3 = 160;

const PARAM_SIZE_L = { titleWidth: COL_WIDTH_0, valueWidth: COL_WIDTH_1 };
const PARAM_SIZE_R = { titleWidth: COL_WIDTH_2, valueWidth: COL_WIDTH_3 };

const DetailsSection = ({
  sx,
}: {
  sx?: SxProps<Theme>,
}) => {
  const selectedTestkit = useSelector(selectSelectedTestkit);
  const role = useSelector(selectRole);
  const email = useSelector(selectEmail);
  const sites = useSelector(selectSiteNames);
  const midwives = useSelector(selectMidwifeNames);

  const [dialogOpenEdit, setDialogOpenEdit] = React.useState(false);
  const [dialogOpenDiscard, setDialogOpenDiscard] = React.useState(false);

  const [hasExtraPaper, setExtraPaper] = React.useState(false);
  const [hasExtraSwab, setExtraSwab] = React.useState(false);

  React.useEffect(() => {
    setExtraPaper(!!selectedTestkit?.additionalPaperLot || !!selectedTestkit?.additionalPaperExpiry);
    setExtraSwab(!!selectedTestkit?.additionalSwabLot || !!selectedTestkit?.additionalSwabExpiry);
  }, [selectedTestkit]);

  // const handleDebug = () => {
  //   if (selectedTestkit) {
  //     TestkitAPI.updateKitData({
  //       testkit: selectedTestkit,
  //       siteId: '6ff8ecc7-e1e8-4cb4-8b0e-c04fc2af82f3',
  //     })
  //     .catch(e => {console.warn(e)});
  //   }
  // }

  const handleEdit = () => {
    setDialogOpenEdit(true);
  }

  const handleDiscard = () => {
    setDialogOpenDiscard(true);
  }

  return (
    <Section sx={{ alignSelf: 'stretch', overflowY: 'auto', ...sx }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', mb: 1, alignItems: 'start' }}>
        <Typography variant='h5'>Kit Details</Typography>

        {selectedTestkit?.dummyFlag && (
          <Typography variant='h5' sx={theme => ({ color: theme.palette.info.main, ml: 2, alignSelf: 'center' })}>DUMMY KIT</Typography>
        )}

        {/* {role === AccountRole.ADMIN && (
          <Button disabled={!selectedTestkit} onClick={handleDebug} sx={{ ml: 'auto' }}>Debug</Button>
        )} */}
        {role === AccountRole.ADMIN && isPowerAdmin(email) && (<>
          <Button
            disabled={!selectedTestkit || selectedTestkit.status === TestkitStatusAPI.NEW}
            onClick={handleEdit}
            sx={{ ml: 'auto', mr: 1 }}
          >Modify</Button>
          <DialogModifyKit open={dialogOpenEdit} testkit={selectedTestkit} onClose={() => setDialogOpenEdit(false)}/>
          
          <Button
            disabled={!selectedTestkit || selectedTestkit.status === TestkitStatusAPI.ISSUED || selectedTestkit.status === TestkitStatusAPI.DONE || selectedTestkit.status === TestkitStatusAPI.DISCARDED}
            onClick={handleDiscard}
          >Discard</Button>
          <DialogDiscardKit open={dialogOpenDiscard} testkit={selectedTestkit} onClose={() => setDialogOpenDiscard(false)}/>
        </>)}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Parameter title='Study ID' primaryValue={selectedTestkit?.id} {...PARAM_SIZE_L}/>
          {!!selectedTestkit?.patient && selectedTestkit?.id !== selectedTestkit?.patient && (
            <Parameter title='Primary ID' primaryValue={selectedTestkit?.patient} {...PARAM_SIZE_L}/>
          )}
          {role !== AccountRole.MEDIC && (
            <Parameter title='Site' primaryValue={selectedTestkit?.site && sites[selectedTestkit.site]} {...PARAM_SIZE_L}/>
          )}
          <Parameter title='Midwife' primaryValue={selectedTestkit?.midwife && midwives[selectedTestkit.midwife]} {...PARAM_SIZE_L}/>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Parameter title='Status' primaryValue={getTestkitStatusLabel(selectedTestkit)} {...PARAM_SIZE_R}/>
          {/* <Parameter2 title='Manufacturer' primaryValue={selectedTestkit?.manufacturer} {...PARAM_SIZE_R}/> */}
          <Parameter title='Location' primaryValue={selectedTestkit && getTestkitLocation({ kit: selectedTestkit, role, sites })} {...PARAM_SIZE_R}/>
          <Parameter title='Dispatched' primaryValue={selectedTestkit?.dispatched && isoDateTimeToDate(selectedTestkit.dispatched)} {...PARAM_SIZE_R}/>
          <Parameter title='Issued' primaryValue={getStatusTimestampDateTime({
            testkit: selectedTestkit,
            status: TestkitStatusAPI.ISSUED,
          })} {...PARAM_SIZE_R}/>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h6'>Test Paper</Typography>

          <Parameter title={hasExtraPaper ? 'Main Lot#' : 'Lot#'} primaryValue={selectedTestkit?.paperLot} {...PARAM_SIZE_L}/>
          <Parameter title={hasExtraPaper ? 'Main Expiry' : 'Expiry'} primaryValue={selectedTestkit?.paperExpiry && isoDateTimeToDate(selectedTestkit.paperExpiry)} {...PARAM_SIZE_L}/>
          
          {hasExtraPaper && (<>
            <Parameter title='Extra Lot#' primaryValue={selectedTestkit?.additionalPaperLot} {...PARAM_SIZE_L} sx={{ mt: 2 }}/>
            <Parameter title='Extra Expiry' primaryValue={selectedTestkit?.additionalPaperExpiry && isoDateTimeToDate(selectedTestkit.additionalPaperExpiry)} {...PARAM_SIZE_L}/>
          </>)}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h6' sx={{ width: COL_WIDTH_2, textAlign: 'right' }}>Swab</Typography>
          
          <Parameter title={hasExtraSwab ? 'Main Lot#' : 'Lot#'} primaryValue={selectedTestkit?.swabLot} {...PARAM_SIZE_R}/>
          <Parameter title={hasExtraSwab ? 'Main Expiry' : 'Expiry'} primaryValue={selectedTestkit?.swabExpiry && isoDateTimeToDate(selectedTestkit.swabExpiry)} {...PARAM_SIZE_R}/>
          
          {hasExtraSwab && (<>
            <Parameter title='Extra Lot#' primaryValue={selectedTestkit?.additionalSwabLot} {...PARAM_SIZE_R} sx={{ mt: 2 }}/>
            <Parameter title='Extra Expiry' primaryValue={selectedTestkit?.additionalSwabExpiry && isoDateTimeToDate(selectedTestkit.additionalSwabExpiry)} {...PARAM_SIZE_R}/>
          </>)}
        </Box>
      </Box>
    </Section>
  );
}

export default DetailsSection;